import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isObject, mapKeys, isEmpty, has } from 'lodash';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { selectHeaderAppUrl, setHeaderNavigation } from 'store/slices/header';
// Import Constants
import { NavigationList } from 'components/Header/constants';

const useGoToRoute = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    // history is a stable object, the current location is read from it on demand, so the consumers of this hook
    // are not re-rendered on every navigation as they would be with useLocation
    const history = useHistory();
    const url = useAppSelector(selectHeaderAppUrl);

    const getRoutePath = useCallback(
        (route, routeParams = undefined, queryString = undefined) => {
            if (!route) {
                return '';
            }

            let { path } = route;
            if (isObject(routeParams)) {
                mapKeys(routeParams, (value, param) => {
                    path = path.replace(`:${param}`, value);
                });
            }
            return !isEmpty(queryString) ? `${url}/${path}${queryString}` : `${url}/${path}`;
        },
        [url],
    );

    const goToRoute = useCallback(
        (route, routeParams = undefined, queryString = undefined, isReplace = false) => {
            const historyAction = isReplace ? history.replace : history.push;
            historyAction(getRoutePath(route, routeParams, queryString), { ...route });
        },
        [getRoutePath, history],
    );

    const goToPath = useCallback(
        (path, isReplace = false) => {
            const historyAction = isReplace ? history.replace : history.push;
            historyAction(path);
        },
        [history],
    );

    const goToPreviousRoute = () => {
        history.goBack();
    };

    const buildRouteObject = (location) => {
        const route: { path?: string; queryString?: string } = {};
        if (has(location, 'pathname') && !isEmpty(location.pathname)) {
            route.path = location.pathname.replace('/crm/', '');
        }
        if (has(location, 'search') && !isEmpty(location.search)) {
            route.queryString = location.search;
        }
        return route;
    };

    const findActiveNavigation = (pathName) => {
        let result = {};
        const entityPathname = pathName.split('/')[2];

        for (const item of NavigationList) {
            if (item.routeModel.path.startsWith(entityPathname)) {
                const { name, path } = item.routeModel;
                result = {
                    name: t(name),
                    route: path,
                };

                break;
            }
        }

        return result;
    };

    const listen = () => {
        const foundActiveNavigation = findActiveNavigation(history.location.pathname);
        dispatch(setHeaderNavigation(foundActiveNavigation));

        return history.listen((location) => {
            const foundActiveNavigation = findActiveNavigation(location.pathname);
            dispatch(setHeaderNavigation(foundActiveNavigation));
        });
    };

    return { goToRoute, goToPreviousRoute, listen, buildRouteObject, getRoutePath, goToPath };
};

export default useGoToRoute;
